import { useMemo, useEffect, useState, Fragment } from "react";

import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Popover from "@mui/material/Popover";
import { Box, TableCell } from "@mui/material";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftButton from "components/SoftComponents/SoftButton";

import RevisedTimelineBodyCell from "./RevisedTimelineBodyCell";
import { Link } from "react-router-dom";
import { useSoftUIController } from "hooks/context";
import moment from "moment";
import SoftPagination from "components/SoftComponents/SoftPagination";
import { useTransition } from "react";
import TimelineSkeleton from "modules/inspections/components/InspectionTimeline/TimelineSkeleton"; // Adjust path as needed

function RevisedTimeline({
  preview = null,
  entriesPerPage = { defaultValue: 20, entries: [20, 40, 60, 80] },
  canSearch = false,
  showTotalEntries = true,
  table,
  pagination = { variant: "gradient", color: "dark" },
  isSorted = true,
  metadata = false,
  currentDevice = null,
  statusFilter = "all",
  setStatusFilter,
  visitTypeFilter = "all",
  setVisitTypeFilter,
  setIsReportModalOpen,
  canCreateReport = false
}) {
  const [controller] = useSoftUIController();
  const { deviceLayout, userPermissions } = controller;

  const [sortingType, setSortingType] = useState("epoch_desc");

  // Popover State (For Mobile View)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const openFilter = Boolean(filterAnchorEl);
  const filterId = openFilter ? "filter-popover" : undefined;
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 20;

  const handleOpenFilterPopover = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => {
    const now = Date.now();

    return table.rows.reduce((accumulator, row) => {
      const matchesStatus = statusFilter === "all" || row.status === statusFilter;
      const matchesVisitType = visitTypeFilter === "all" || row.visitType == "both" || row.visitType === visitTypeFilter;

      if (matchesStatus && matchesVisitType) {
        const date = new Date(row.epoch * 1000);

        accumulator.push({
          ...row,
          day: String(date.getDate()).padStart(2, '0'),
          month: String(date.getMonth() + 1).padStart(2, '0'),
          isFuture: date.getTime() > now
        });
      }

      return accumulator;
    }, []);
  }, [table.rows, statusFilter, visitTypeFilter]);

  // Custom Global Filter method for react-table text search
  const customGlobalFilter = useMemo(() => {
    return (rows, columnIds, filterValue) => {
      if (!filterValue) return rows;
      const searchLower = String(filterValue).toLowerCase();

      return rows.filter((row) => row.original.searchStr.includes(searchLower));
    };
  }, []);

  const tableInstance = useTable(
    {
      columns,
      data,
      globalFilter: customGlobalFilter,
      autoResetHiddenColumns: false,
      autoResetSortBy: false,
      autoResetGlobalFilter: false,
      getRowId: (row) => row.itemData.inspectionData._id,
      initialState: {
        pageIndex: 0,
        pageSize: defaultValue
      }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    setSortBy,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  useEffect(() => setPageSize(defaultValue || 20), [defaultValue]);

  // Set default sorting on mount
  useEffect(() => {
    setSortBy([{ id: "epoch", desc: true }]);
    setSortingType("epoch_desc");
  }, [isSorted, setSortBy]);

  // Search input state
  const [search, setSearch] = useState(globalFilter || "");
  const [isSearching, setIsSearching] = useState(false);

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
    // Add a tiny extra timeout to ensure React finishes drawing the real list before hiding the skeleton
    setTimeout(() => setIsSearching(false), 50);
  }, 300);

  const canInspectionsAdd = userPermissions.indexOf("operations-inspections:add") >= 0 && metadata.timeline_type === "inspection";
  const showUnderReview = userPermissions.includes("operations:reviewer");

  // Toggle sorting
  const handleToggleSort = () => {
    const nextSortType = sortingType === "epoch_desc" ? "epoch_asce" : "epoch_desc";
    const isDesc = nextSortType === "epoch_desc";

    setSortBy([{ id: "epoch", desc: isDesc }]);
    setSortingType(nextSortType);
  };

  // Toggle status filter
  const handleToggleStatusFilter = () => {
    let nextStatus = "all";

    if (statusFilter === "all") nextStatus = showUnderReview ? "review" : "inspected";
    else if (statusFilter === "review") nextStatus = "inspected";
    else if (statusFilter === "inspected") nextStatus = "resolved";
    else if (statusFilter === "resolved") nextStatus = "unresolved";
    else if (statusFilter === "unresolved") nextStatus = "all";

    if (setStatusFilter) {
      setStatusFilter(nextStatus);
    }
  };

  const getStatusFilterLabel = () => {
    if (statusFilter === "unresolved") return { label: "Unresolved", color: 'error' };
    if (statusFilter === "review") return { label: deviceLayout == "mobile" ? "Reviewing" : "Under Review", color: 'warning' };
    if (statusFilter === "inspected") return { label: "Inspected", color: 'info' };
    if (statusFilter === "resolved") return { label: "Resolved", color: 'success' };
    return { label: "All", color: 'white' };
  };

  // Toggle visit type filter
  const handleToggleVisitTypeFilter = () => {
    let nextType = "all";

    if (visitTypeFilter === "all") nextType = "callout";
    else if (visitTypeFilter === "callout") nextType = "routine";
    else if (visitTypeFilter === "routine") nextType = "all";

    if (setVisitTypeFilter) {
      setVisitTypeFilter(nextType);
    }
  };

  const getVisitTypeFilterLabel = () => {
    if (visitTypeFilter === "callout") return { label: "Callout", color: 'warning' };
    if (visitTypeFilter === "routine") return { label: "Routine", color: 'info' };
    return { label: "All", color: 'white' };
  };

  const renderPagination = pageOptions.map((option) => (
    <SoftPagination item key={option} onClick={() => gotoPage(Number(option))} active={pageIndex === option}>{option + 1}</SoftPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) => value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));
  const customizedPageOptions = pageOptions.map((option) => option + 1);
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;
  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer sx={{ boxShadow: "none", height: "100%" }}>
      <SoftBox
        display="flex"
        flexDirection="column"
        borderRadius="16px"
        color="white"
        width="100%"
        height="100%"
        sx={({ functions: { rgba } }) => ({
          backgroundColor: rgba("#eeeeee", 1),
          overflow: "hidden",
          minWidth: 0,
          width: "100%",
        })}
      >
        {/* Navigation / Header Bar */}
        {(canSearch || canInspectionsAdd) && (
          <SoftBox
            py={1}
            px={{ xxs: 1, xl: 2 }}
            sx={({ functions: { rgba } }) => ({
              borderRadius: "16px 16px 0 0",
              backgroundColor: rgba("#dcdcdc", 1),
              borderBottom: "1px solid #8e98a5",
            })}
          >
            <SoftBox
              display="flex"
              flexDirection={{ xxs: "column", xl: "row" }}
              justifyContent={{ xl: "space-between" }}
              gap={1.5}
            >
              {/* DESKTOP LEFT GROUP: Filter Buttons (Hidden on Mobile) */}
              <SoftBox
                display={{ xxs: "none", xl: "flex" }}
                flexDirection="row"
                alignItems="flex-end"
                gap={1}
                flex="1 1 auto"
              >
                {/* Sort Toggle Button */}
                <SoftBox flex={{ xl: "0 0 100px" }} minWidth={0}>
                  <SoftBox mb={0.5} pl={0.5}>
                    <SoftTypography variant="caption" fontWeight="bold">Sort By</SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="contained"
                    color="white"
                    size="small"
                    onClick={handleToggleSort}
                    fullWidth
                    sx={{
                      height: "40px !important",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      textTransform: "none",
                      px: 1.5,
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: "1.1rem !important" }}>
                      {sortingType === "epoch_desc" ? "arrow_downward" : "arrow_upward"}
                    </Icon>
                    {sortingType === "epoch_desc" ? "Date (Desc)" : "Date (Asc)"}
                  </SoftButton>
                </SoftBox>

                {/* Inspection Status Filter Button */}
                <SoftBox flex={{ xl: "0 0 105px" }} minWidth={0}>
                  <SoftBox mb={0.5} pl={0.5}>
                    <SoftTypography variant="caption" fontWeight="bold">Status</SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant={statusFilter === "all" ? "contained" : "gradient"}
                    color={getStatusFilterLabel().color}
                    size="small"
                    onClick={handleToggleStatusFilter}
                    fullWidth
                    sx={{
                      height: "40px !important",
                      boxShadow: statusFilter === "all" ? "0 1px 2px rgba(0,0,0,0.05)" : undefined,
                      textTransform: "none",
                      px: 1.5,
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: "1.1rem !important" }}>
                      {statusFilter === "all" ? "filter_alt_off" : "filter_alt"}
                    </Icon>
                    {getStatusFilterLabel().label}
                  </SoftButton>
                </SoftBox>

                {/* Visit Type Filter Button */}
                <SoftBox flex={{ xl: "0 0 90px" }} minWidth={0}>
                  <SoftBox mb={0.5} pl={0.5}>
                    <SoftTypography variant="caption" fontWeight="bold">Visit Type</SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant={visitTypeFilter === "all" ? "contained" : "gradient"}
                    color={getVisitTypeFilterLabel().color}
                    size="small"
                    onClick={handleToggleVisitTypeFilter}
                    fullWidth
                    sx={{
                      height: "40px !important",
                      boxShadow: visitTypeFilter === "all" ? "0 1px 2px rgba(0,0,0,0.05)" : undefined,
                      textTransform: "none",
                      px: 1.5,
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: "1.1rem !important" }}>
                      {visitTypeFilter === "all" ? "filter_alt_off" : "filter_alt"}
                    </Icon>
                    {getVisitTypeFilterLabel().label}
                  </SoftButton>
                </SoftBox>

                {/* Create Report Button INCOMPLETE */}
                {canCreateReport && (
                  <SoftBox flex={{ xl: "0 0 40px" }} minWidth={0}>
                    <SoftButton
                      variant={"gradient"}
                      color="dark"
                      size="small"
                      onClick={() => setIsReportModalOpen(true)}
                      fullWidth
                      sx={{
                        height: "40px !important",
                        p: 0,
                        minWidth: 0
                      }}
                    >
                      <Icon sx={{ color: "#fff", fontSize: '20px !important', '&.MuiIcon-root': { fontSize: '20px !important' } }}>
                        note_add
                      </Icon>
                    </SoftButton>
                  </SoftBox>
                )}
              </SoftBox>


              {/* RIGHT GROUP: Search & New Inspection (Plus Mobile Popover Trigger) */}
              <SoftBox
                display="flex"
                flexDirection="row"
                alignItems="flex-end"
                gap={1}
                flex={{ xxs: "1 1 100%", xl: "0 1 auto" }}
                width={{ xxs: "100%", xl: "auto" }}
              >
                {/* POPOVER TOGGLE BUTTON (Hidden on Desktop, Visible on Mobile) */}
                <SoftBox display={{ xxs: "block", xl: "none" }} flex={{ xxs: "0 0 40px" }} minWidth={0}>
                  <SoftButton
                    aria-describedby={filterId}
                    variant={"gradient"}
                    color="dark"
                    size="small"
                    onClick={handleOpenFilterPopover}
                    fullWidth
                    sx={{
                      height: "40px !important",
                      p: 0,
                      minWidth: 0
                    }}
                  >
                    <Icon sx={{ color: "#fff", fontSize: '20px !important', '&.MuiIcon-root': { fontSize: '20px !important' } }}>
                      tune
                    </Icon>
                  </SoftButton>
                </SoftBox>

                {/* MOBILE FILTER POPOVER COMPONENT */}
                <Popover
                  id={filterId}
                  open={openFilter}
                  anchorEl={filterAnchorEl}
                  onClose={handleCloseFilterPopover}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  PaperProps={{
                    sx: { borderRadius: 3, mt: 1, boxShadow: 4, backgroundColor: "rgba(238, 238, 238, 0.95) !important" }
                  }}
                >
                  <SoftBox px={1} pb={1} display="flex" flexDirection="column" gap={1} width="240px">
                    {/* Sort Toggle Button */}
                    <SoftBox>
                      <SoftBox mb={0.5} pl={0.5}>
                        <SoftTypography variant="caption" fontWeight="bold">Sort By</SoftTypography>
                      </SoftBox>
                      <SoftButton
                        variant="contained"
                        color="white"
                        size="small"
                        onClick={handleToggleSort}
                        fullWidth
                        sx={{
                          height: "32px !important",
                          textTransform: "none",
                          justifyContent: "flex-start",
                          px: 1.5,
                        }}
                      >
                        <Icon sx={{ mr: 1, fontSize: "1.1rem !important" }}>
                          {sortingType === "epoch_desc" ? "arrow_downward" : "arrow_upward"}
                        </Icon>
                        {sortingType === "epoch_desc" ? "Date (Desc)" : "Date (Asc)"}
                      </SoftButton>
                    </SoftBox>

                    <SoftBox display="flex" justifyContent="center" alignItems="center" gap={1}>
                      {/* Inspection Status Filter Button */}
                      <SoftBox width="100%">
                        <SoftBox mb={0.5} pl={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">Status</SoftTypography>
                        </SoftBox>
                        <SoftButton
                          variant={statusFilter === "all" ? "contained" : "gradient"}
                          color={getStatusFilterLabel().color}
                          size="small"
                          onClick={handleToggleStatusFilter}
                          fullWidth
                          sx={{
                            height: "32px !important",
                            textTransform: "none",
                            justifyContent: "flex-start",
                            px: 1.5,
                          }}
                        >
                          <Icon sx={{ mr: 1, fontSize: "1.1rem !important" }}>
                            {statusFilter === "all" ? "filter_alt_off" : "filter_alt"}
                          </Icon>
                          {getStatusFilterLabel().label}
                        </SoftButton>
                      </SoftBox>

                      {/* Visit Type Filter Button */}
                      <SoftBox width="100%">
                        <SoftBox mb={0.5} pl={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">Visit Type</SoftTypography>
                        </SoftBox>
                        <SoftButton
                          variant={visitTypeFilter === "all" ? "contained" : "gradient"}
                          color={getVisitTypeFilterLabel().color}
                          size="small"
                          onClick={handleToggleVisitTypeFilter}
                          fullWidth
                          sx={{
                            height: "32px !important",
                            textTransform: "none",
                            justifyContent: "flex-start",
                            px: 1.5,
                          }}
                        >
                          <Icon sx={{ mr: 1, fontSize: "1.1rem !important" }}>
                            {visitTypeFilter === "all" ? "filter_alt_off" : "filter_alt"}
                          </Icon>
                          {getVisitTypeFilterLabel().label}
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>

                    {/* Create Report Button INCOMPLETE */}
                    {canCreateReport && (
                      <SoftBox pt={1}>
                        <SoftButton
                          variant={"gradient"}
                          color="secondary"
                          size="small"
                          onClick={() => {
                            setIsReportModalOpen(true);
                            handleCloseFilterPopover();
                          }}
                          fullWidth
                          sx={{
                            height: "32px !important",
                            justifyContent: "flex-start",
                            px: 1.5,
                          }}
                        >
                          <Icon sx={{ color: "#fff", mr: 1, fontSize: '1.2rem !important' }}>
                            note_add
                          </Icon>
                          Create Report
                        </SoftButton>
                      </SoftBox>
                    )}
                  </SoftBox>
                </Popover>

                {/* Search Field */}
                {canSearch && (
                  <SoftBox flex={{ xxs: canInspectionsAdd ? "3 1 0" : "1 1 100%", md: "1 1 235px", xl: "0 0 270px" }}>
                    <SoftBox sx={{ background: "#fff", borderRadius: "0.5rem", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
                      <SoftInput
                        placeholder="Search entries..."
                        icon={{ component: "search", direction: "left" }}
                        value={search}
                        onChange={({ currentTarget }) => {
                          setSearch(currentTarget.value);
                          setIsSearching(true);
                          onSearchChange(currentTarget.value);
                        }}
                        sx={({ typography: { fontFamily } }) => ({
                          fontFamily,
                        })}
                      />
                    </SoftBox>
                  </SoftBox>
                )}

                {/* New Inspection Action Button */}
                {canInspectionsAdd && (
                  <SoftBox flex={{ xxs: canSearch ? "1 1 0" : "1 1 100%", md: "0 0 auto" }} minWidth={{ md: "100px" }}>
                    <SoftButton
                      variant="gradient"
                      size="small"
                      color="dark"
                      component={Link}
                      to="/operations/inspections/new"
                      state={{ currentDevice }}
                      fullWidth
                      sx={{
                        whiteSpace: "nowrap",
                        height: "40px !important",
                        p: { xxs: "0.5rem !important", xl: "0.5rem 2rem !important" }
                      }}
                    >
                      <Icon sx={{ mr: 0.5, fontWeight: "bold" }}>add</Icon>
                      {deviceLayout === "mobile" ? "New" : "New Inspection"}
                    </SoftButton>
                  </SoftBox>
                )}
              </SoftBox>
            </SoftBox>
          </SoftBox>
        )}

        {/* Table Body Area */}
        <SoftBox height="auto" sx={{ overflowY: "hidden", position: 'relative' }} flex={1}>
          <SoftBox display="flex" height="100%" flex={1} sx={{ overflowX: "auto", overflowY: "hidden" }}>
            <Box
              flex={{ xxs: 12, md: metadata.selectedInspection != null ? 5 : 12 }}
              pb={{ xxs: 1, md: 0 }}
              px={{ xxs: 1, md: metadata.selectedInspection != null ? 0.5 : 2 }}
              height="calc(100% - 16px)"
              sx={{ overflowY: "auto", p: 2 }}
            >
              <Table {...getTableProps()} sx={{ margin: "auto", width: "100%", maxWidth: "800px" }}>
                <SoftBox component="thead" sx={{ borderBottom: "2px solid #e0e0e0", backgroundColor: "#f8f9fa" }} />
                <TableBody {...getTableBodyProps()}>
                  {(isSearching || metadata.queriesLoading) ? (
                    // SHOW 5 SKELETONS WHILE SEARCHING
                    Array.from(new Array(7)).map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        <TableCell colSpan={120} sx={{ border: 'none', padding: 0 }}>
                          <TimelineSkeleton />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : page.length > 0 ? (
                    page.map((row, visualIndex) => {
                      prepareRow(row);
                      const { key: rowKey, ...getRowProps } = row.getRowProps();

                      var identifiers = {
                        day: visualIndex === 0 || row.original.day !== page[visualIndex - 1].original.day,
                        month: visualIndex === page.length - 1 || row.original.month !== page[visualIndex + 1].original.month,
                        status: false,
                      };

                      const firstFuture = (visualIndex === page.length - 1 || !page[visualIndex + 1].original.isFuture) && row.original.isFuture;

                      return (
                        <Fragment key={rowKey}>
                          <TableRow
                            {...getRowProps}
                            sx={({ borders: { borderWidth, borderColor } }) => ({
                              backgroundColor: "transparent",
                              borderBottom:
                                !firstFuture &&
                                identifiers.month &&
                                visualIndex !== page.length - 1 &&
                                `${borderWidth[2]} solid ${borderColor}`,
                            })}
                          >
                            {row.cells
                              .filter((cell) => !cell.column.hidden)
                              .map((cell) => {
                                const { key: cellKey, ...cellProps } = cell.getCellProps();

                                return (
                                  <RevisedTimelineBodyCell
                                    key={cellKey}
                                    {...cellProps}
                                    leftBorder={cell.column.id === "epoch"}
                                    maxWidth={cell.column.id === "epoch" ? { xxs: "0", sm: "60px" } :  { xxs: "100%", sm: "100%" }}
                                  >
                                    {cell.render("Cell", {
                                      sortBy: sortingType,
                                      identifiers,
                                      visualIndex,
                                      pageSize: page.length,
                                      firstFuture,
                                      selectedInspectionId: metadata.selectedInspection?._id || null,
                                      setSelectedInspection: metadata.setSelectedInspection
                                    })}
                                  </RevisedTimelineBodyCell>
                                );
                              })}
                          </TableRow>

                          {/* Render a dedicated divider row for TODAY when this row is the first future item */}
                          {firstFuture && (
                            <TableRow key={`today-divider-${visualIndex}`}>
                              <TableCell
                                colSpan={row.cells.filter((c) => !c.column.hidden).length}
                                sx={{
                                  padding: "16px 0",
                                  border: "none",
                                }}
                              >
                                <SoftBox
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                    width: "100%",
                                    "&::before": {
                                      content: '""',
                                      position: "absolute",
                                      left: 0,
                                      right: 0,
                                      top: "50%",
                                      borderBottom: "2px solid #e0e0e0",
                                      zIndex: 1,
                                    },
                                  }}
                                >
                                  <SoftBox
                                    sx={{
                                      position: "relative",
                                      zIndex: 2,
                                      backgroundColor: "#ffffff",
                                      padding: "2px 12px",
                                      borderRadius: "12px",
                                      border: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <SoftTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="text"
                                      sx={{ textTransform: "uppercase", letterSpacing: "1px" }}
                                    >
                                      TODAY
                                    </SoftTypography>
                                  </SoftBox>
                                </SoftBox>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })
                  ) : (
                    <SoftBox p={2} pl={3}>
                      <SoftTypography variant="h5" textAlign="center">
                        {metadata.queriesError ? "Error loading inspection history." : (metadata.queriesLoading ? "Loading history..."
                          : metadata.timeline_type.includes("inspection") ? "History not found." : "No active assignments found.")}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </TableBody>
              </Table>

              <SoftBox
                display="flex"
                flexDirection={"column"}
                justifyContent="space-between"
                alignItems={"center"}
                my={1}
                gap={2}
                p={!showTotalEntries && pageOptions.length === 1 ? 0 : 2}
              >
                {(showTotalEntries && !metadata.queriesLoading && page.length > 0) && (
                  <SoftBox mb={{ xxs: 3, sm: 0 }}>
                    <SoftTypography variant="button" color="secondary" fontWeight="regular">
                      Showing {entriesStart} to {entriesEnd > rows.length ? rows.length : entriesEnd} of {rows.length} entries
                    </SoftTypography>
                  </SoftBox>
                )}
                {pageOptions.length > 1 && (
                  <SoftPagination
                    variant={pagination.variant ? pagination.variant : "gradient"}
                    color={pagination.color ? pagination.color : "info"}
                  >
                    {canPreviousPage && (
                      <SoftPagination item onClick={() => previousPage()}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </SoftPagination>
                    )}
                    {renderPagination.length > 6 ? (
                      <SoftBox width="5rem" mx={1}>
                        <SoftInput
                          inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                          value={customizedPageOptions[pageIndex]}
                          onChange={(handleInputPagination, handleInputPaginationValue)}
                          sx={({ typography: { fontFamily } }) => ({ fontFamily })}
                        />
                      </SoftBox>
                    ) : (
                      renderPagination
                    )}
                    {canNextPage && (
                      <SoftPagination item onClick={() => {
                        nextPage();
                        window.scrollTo({ top: 0 });
                      }}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </SoftPagination>
                    )}
                  </SoftPagination>
                )}
              </SoftBox>
            </Box>

            {/* Preview Box */}
            <Box
              flex={{ xxs: 0, md: metadata.selectedInspection != null ? 7 : 0 }}
              p={{ xxs: 0, md: metadata.selectedInspection != null ? 2 : 0 }}
            >
              {preview}
            </Box>
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </TableContainer>
  );
}

RevisedTimeline.propTypes = {
  preview: PropTypes.node,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  metadata: PropTypes.object,
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  statusFilter: PropTypes.string,
  setStatusFilter: PropTypes.func,
  visitTypeFilter: PropTypes.string,
  setVisitTypeFilter: PropTypes.func,
  setIsReportModalOpen: PropTypes.func,
  canCreateReport: PropTypes.bool,
};

export default RevisedTimeline;